import { useState, useEffect } from 'react';
import { usePagedList } from '../../hooks/usePagedList.js';
import { useApi } from '../../hooks/useApi.js';
import { getMembershipLevels } from '../../api/membershipLevelsApi.js';
import SortableTh from '../../components/ui/SortableTh.jsx';
import Pagination from '../../components/ui/Pagination.jsx';
import {
  adminGetVirtuals, adminGetVirtual, adminCreateVirtual, adminUpdateVirtual, adminDeleteVirtual,
  adminGenerateShareToken, adminRevokeShareToken,
} from '../../api/virtualApi.js';
import {
  VIRTUAL_MACHINES, VIRTUAL_MACHINE_LABELS,
  VIRTUAL_CATEGORIES, VIRTUAL_CATEGORY_LABELS,
  VIRTUAL_VALIDITY, VIRTUAL_VALIDITY_LABELS,
} from '../../data/virtual-products.js';
import { useToast } from '../../context/ToastContext.jsx';
import './AdminUploadPage.css';

// 沒有圖片時的預設縮圖底色
const DEFAULT_THUMB_BG = 'linear-gradient(135deg, #1B1B1B 0%, #3B3B3B 100%)';

const STATUS_LABEL = { active: '上架中', draft: '草稿' };

function AdminVirtualPage() {
  const { showToast } = useToast();
  const [mode, setMode] = useState('list'); // list | form
  const [editId, setEditId] = useState(null);

  return (
    <>
      <div className="admin-page-head">
        <h1>
          虛擬商品管理
          <small>可登記的機台主題 · 設定種類/角色與各自價格</small>
        </h1>
      </div>

      {mode === 'list'
        ? <VirtualList
            onAdd={() => { setEditId(null); setMode('form'); }}
            onEdit={(id) => { setEditId(id); setMode('form'); }}
            showToast={showToast}
          />
        : <VirtualForm
            editId={editId}
            onDone={() => setMode('list')}
            onCancel={() => setMode('list')}
            showToast={showToast}
          />
      }
    </>
  );
}

// ── 列表 ──────────────────────────────────────────────
function VirtualList({ onAdd, onEdit, showToast }) {
  const list = usePagedList(p => adminGetVirtuals(p), {
    limit: 10, initialSort: 'createdAt,desc', initialFilters: { machine: 'all', status: '' },
  });
  const { items, totalPages, page, setPage, sort, toggleSort, filters, setFilter, q, setQ, loading } = list;

  const handleDelete = async (v) => {
    if (!window.confirm(`確定刪除「${v.name}」？`)) return;
    try {
      await adminDeleteVirtual(v.id);
      showToast(`已刪除：${v.name}`, 'success');
      list.refetch();
    } catch (err) {
      showToast(`刪除失敗：${err.message}`, 'error');
    }
  };

  return (
    <>
      <div className="admin-card" style={{ marginBottom: 16, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input className="au-search" placeholder="搜尋名稱 / 副標 / slug…" value={q} onChange={e => setQ(e.target.value)} />
          <select className="admin-select" value={filters.machine} onChange={e => setFilter('machine', e.target.value)}>
            <option value="all">全部機台</option>
            {Object.values(VIRTUAL_MACHINES).map(m => <option key={m} value={m}>{VIRTUAL_MACHINE_LABELS[m]}</option>)}
          </select>
          <select className="admin-select" value={filters.status} onChange={e => setFilter('status', e.target.value)}>
            <option value="">全部狀態</option>
            <option value="active">上架中</option>
            <option value="draft">草稿</option>
          </select>
        </div>
        <button className="admin-btn primary" onClick={onAdd}>＋ 新增虛擬商品</button>
      </div>
      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: 72 }}>縮圖</th>
              <SortableTh field="name" sort={sort} onSort={toggleSort}>名稱</SortableTh>
              <SortableTh field="machine" sort={sort} onSort={toggleSort} style={{ width: 150 }}>機台 / 類型</SortableTh>
              <SortableTh field="price" sort={sort} onSort={toggleSort} style={{ width: 120 }}>價格</SortableTh>
              <SortableTh field="status" sort={sort} onSort={toggleSort} style={{ width: 90 }}>狀態</SortableTh>
              <th style={{ width: 140 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0
              ? <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</td></tr>
              : items.length === 0
                ? <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>尚無虛擬商品，點右上角新增</td></tr>
                : items.map(v => {
                  const prices = (v.variants || []).map(x => x.price);
                  const priceText = prices.length
                    ? (Math.min(...prices) === Math.max(...prices) ? `NT$${prices[0]}` : `NT$${Math.min(...prices)}~${Math.max(...prices)}`)
                    : `NT$${v.price ?? 0}`;
                  return (
                    <tr key={v.id}>
                      <td>
                        <div className="au-thumb" style={(v.images?.[0] || v.thumbUrl)
                          ? { backgroundImage: `url(${v.images?.[0] || v.thumbUrl})`, backgroundSize: 'cover' }
                          : { background: DEFAULT_THUMB_BG }} />
                      </td>
                      <td>
                        <div style={{ font: '700 14px "Noto Sans TC"', color: 'var(--navy)' }}>{v.name}</div>
                        <div style={{ font: '500 11px Inter', color: 'var(--ink-soft)' }}>{v.slug}</div>
                      </td>
                      <td>
                        <span className={`chip ${v.machine === 'D3' ? 'active' : ''}`}>{VIRTUAL_MACHINE_LABELS[v.machine] || v.machine}</span>
                        <div style={{ font: '500 11px "Noto Sans TC"', color: 'var(--ink-soft)', marginTop: 2 }}>{VIRTUAL_CATEGORY_LABELS[v.category] || v.category}</div>
                      </td>
                      <td style={{ font: '700 14px Inter', color: 'var(--navy)' }}>{priceText}</td>
                      <td><span className={`chip ${v.status === 'active' ? 'active' : ''}`}>{STATUS_LABEL[v.status] || v.status}</span></td>
                      <td>
                        <div className="actions">
                          <button className="admin-btn ghost sm" onClick={() => onEdit(v.id)}>編輯</button>
                          <button className="admin-btn danger sm" onClick={() => handleDelete(v)}>刪除</button>
                        </div>
                      </td>
                    </tr>
                  );
                })
            }
          </tbody>
        </table>
      </div>
      <Pagination page={page} totalPages={totalPages} onChange={setPage} />
    </>
  );
}

// ── 表單（新增 / 編輯）──────────────────────────────────
const emptyForm = {
  name: '', machine: 'D3', category: 'theme', validity: 'permanent',
  price: 0, description: '', images: [], previewUrls: [],
  status: 'active', minLevel: 0, tagNew: false, tagLimited: false,
  unpublishAt: '',
  variants: [], shareToken: null,
};

function VirtualForm({ editId, onDone, onCancel, showToast }) {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(!!editId);
  const [saving, setSaving] = useState(false);
  const { data: levels = [] } = useApi(() => getMembershipLevels(), []);

  useEffect(() => {
    if (!editId) { setForm(emptyForm); return; }
    let alive = true;
    setLoading(true);
    adminGetVirtual(editId).then(v => {
      if (!alive || !v) return;
      setForm({
        name: v.name || '', machine: v.machine || 'D3',
        category: v.category || 'theme', validity: v.validity || 'permanent',
        price: v.price ?? 0,
        // 舊資料只有 thumbUrl 時轉進 images 一起編輯（後端讀取也做同樣 fallback）
        description: v.description || '',
        images: (v.images && v.images.length) ? v.images : (v.thumbUrl ? [v.thumbUrl] : []),
        // 舊資料只有單支 previewUrl 時轉進 previewUrls 一起編輯（後端讀取也做同樣 fallback）
        previewUrls: (v.previewUrls && v.previewUrls.length) ? v.previewUrls : (v.previewUrl ? [v.previewUrl] : []),
        status: v.status || 'active',
        unpublishAt: v.unpublishAt ? String(v.unpublishAt).slice(0, 16) : '',
        minLevel: v.minLevel ?? 0,
        tagNew: (v.tags || []).includes('new'), tagLimited: (v.tags || []).includes('limited'),
        variants: (v.variants || []).map(x => ({ name: x.name, price: x.price })),
        shareToken: v.shareToken ?? null,
      });
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [editId]);

  const set = (k, val) => setForm(f => ({ ...f, [k]: val }));
  const setVariant = (i, k, val) => setForm(f => ({ ...f, variants: f.variants.map((v, idx) => idx === i ? { ...v, [k]: val } : v) }));
  const addVariant = () => setForm(f => ({ ...f, variants: [...f.variants, { name: '', price: '' }] }));
  const removeVariant = (i) => setForm(f => ({ ...f, variants: f.variants.filter((_, idx) => idx !== i) }));
  // 分享連結：產生（免登入可看）/ 複製 / 停用。舊連結在重新產生或停用後即失效。
  const shareUrl = form.shareToken ? `${window.location.origin}/v/${form.shareToken}` : '';
  const genShare = async () => {
    try {
      const v = await adminGenerateShareToken(editId);
      set('shareToken', v.shareToken);
      showToast(form.shareToken ? '已重新產生分享連結（舊連結失效）' : '已產生分享連結', 'success');
    } catch (err) {
      showToast(`產生失敗：${err.message}`, 'error');
    }
  };
  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showToast('已複製分享連結', 'success');
    } catch {
      showToast('複製失敗，請手動選取複製', 'error');
    }
  };
  const revokeShare = async () => {
    if (!window.confirm('停用後這條分享連結立即失效，確定？')) return;
    try {
      await adminRevokeShareToken(editId);
      set('shareToken', null);
      showToast('已停用分享連結', 'success');
    } catch (err) {
      showToast(`停用失敗：${err.message}`, 'error');
    }
  };

  const setImage = (i, val) => setForm(f => ({ ...f, images: f.images.map((u, idx) => idx === i ? val : u) }));
  const addImage = () => setForm(f => ({ ...f, images: [...f.images, ''] }));
  const removeImage = (i) => setForm(f => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const setPreviewUrl = (i, val) => setForm(f => ({ ...f, previewUrls: f.previewUrls.map((u, idx) => idx === i ? val : u) }));
  const addPreviewUrl = () => setForm(f => ({ ...f, previewUrls: [...f.previewUrls, ''] }));
  const removePreviewUrl = (i) => setForm(f => ({ ...f, previewUrls: f.previewUrls.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!form.name.trim()) { showToast('請輸入名稱', 'error'); return; }
    const tags = [];
    if (form.tagNew) tags.push('new');
    if (form.tagLimited) tags.push('limited');
    const payload = {
      name: form.name.trim(), machine: form.machine,
      category: form.category, validity: form.validity,
      price: Number(form.price) || 0, description: form.description,
      images: form.images.map(u => u.trim()).filter(Boolean),
      previewUrls: form.previewUrls.map(u => u.trim()).filter(Boolean),
      status: form.status, minLevel: form.minLevel, tags,
      unpublishAt: form.unpublishAt || '',
      variants: form.variants
        .filter(v => v.name.trim())
        .map((v, i) => ({ name: v.name.trim(), price: Number(v.price) || 0, sortOrder: i })),
    };
    // 變體價格檢查
    if (payload.variants.some(v => v.price <= 0)) { showToast('每個變體售價都要大於 0', 'error'); return; }
    if (!payload.variants.length && payload.price <= 0) { showToast('沒有變體時，請填基準售價', 'error'); return; }
    if (payload.status === 'active' && payload.unpublishAt && new Date(payload.unpublishAt).getTime() <= Date.now()) {
      showToast('\u81ea\u52d5\u4e0b\u67b6\u6642\u9593\u5fc5\u9808\u665a\u65bc\u73fe\u5728', 'error');
      return;
    }

    setSaving(true);
    try {
      if (editId) {
        await adminUpdateVirtual(editId, payload);
        showToast('已更新虛擬商品', 'success');
      } else {
        await adminCreateVirtual(payload);
        showToast('已新增虛擬商品', 'success');
      }
      onDone();
    } catch (err) {
      showToast(`儲存失敗：${err.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="admin-card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</div>;

  return (
    <div className="admin-form admin-card">
      <p style={{ font: '600 13px "Noto Sans TC"', color: 'var(--ink-soft)', margin: '0 0 20px' }}>
        圖片 / 預覽影片本期先<b>貼網址</b>，待之後接上傳功能改為直接上傳。圖片與影片都可加多筆，前台詳情頁會變成圖庫（影片排在圖片前面）。設了「種類/角色」變體時，前台價格以變體為準；沒設變體則用基準售價。
      </p>

      <div className="au-form-grid">
        <div className="au-media-col">
          <div className="field">
            <label>商品圖片（可多張，第一張為列表縮圖）</label>
            <div className="au-preview" style={form.images.find(u => u.trim())
              ? { backgroundImage: `url(${form.images.find(u => u.trim())})`, backgroundSize: 'cover' }
              : { background: DEFAULT_THUMB_BG }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
              {form.images.map((u, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input style={{ flex: 1 }} type="text" placeholder={`https://example.com/img-${i + 1}.jpg`} value={u} onChange={e => setImage(i, e.target.value)} />
                  <button type="button" className="admin-btn danger sm" onClick={() => removeImage(i)}>移除</button>
                </div>
              ))}
              <button type="button" className="admin-btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={addImage}>＋ 新增圖片</button>
            </div>
          </div>
          <div className="field">
            <label>預覽影片網址（選填，可多支）</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {form.previewUrls.map((u, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input style={{ flex: 1 }} type="text" placeholder={`https://example.com/preview-${i + 1}.mp4`} value={u} onChange={e => setPreviewUrl(i, e.target.value)} />
                  <button type="button" className="admin-btn danger sm" onClick={() => removePreviewUrl(i)}>移除</button>
                </div>
              ))}
              <button type="button" className="admin-btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={addPreviewUrl}>＋ 新增影片</button>
            </div>
            <small style={{ color: 'var(--ink-soft)', fontSize: 12 }}>建議 5–8 秒 / 16:10 / mp4。第一支為主預覽，列表卡 hover 會播它。</small>
          </div>
        </div>

        <div>
          <div className="field"><label>商品名稱 *</label><input type="text" placeholder="極光主題 AURORA" value={form.name} onChange={e => set('name', e.target.value)} /></div>

          <div className="field">
            <label>相容機台 *</label>
            <div className="au-toggle-group">
              {Object.values(VIRTUAL_MACHINES).map(m => (
                <button key={m} type="button" className={form.machine === m ? 'on' : ''} onClick={() => set('machine', m)}>{VIRTUAL_MACHINE_LABELS[m]}</button>
              ))}
            </div>
          </div>

          <div className="row3">
            <div className="field">
              <label>內容類型</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}>
                {Object.values(VIRTUAL_CATEGORIES).map(c => <option key={c} value={c}>{VIRTUAL_CATEGORY_LABELS[c]}</option>)}
              </select>
            </div>
            <div className="field">
              <label>效期</label>
              <select value={form.validity} onChange={e => set('validity', e.target.value)}>
                {Object.values(VIRTUAL_VALIDITY).map(v => <option key={v} value={v}>{VIRTUAL_VALIDITY_LABELS[v]}</option>)}
              </select>
            </div>
            <div className="field"><label>基準售價 NT$</label><input type="number" min="0" step="1" placeholder="280" value={form.price} onChange={e => set('price', e.target.value)} /></div>
          </div>

          <div className="field">
            <label>最低可見等級</label>
            <select value={form.minLevel} onChange={e => set('minLevel', Number(e.target.value))}>
              {levels.map(l => <option key={l.id} value={l.level}>{l.name}</option>)}
            </select>
            <small style={{ display: 'block', marginTop: 6, color: 'var(--ink-soft)', fontSize: 12 }}>
              會員等級 ≥ 這個等級才看得到（門檻制）。預設「一般」= 所有人可見。
            </small>
          </div>

          {/* 分享連結：免登入即可檢視此商品（繞過會員制與等級）。僅已存檔商品可用。 */}
          {editId && (
            <div className="field">
              <label>分享連結（免登入可看）</label>
              {form.shareToken ? (
                <>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input style={{ flex: 1, minWidth: 220 }} type="text" readOnly value={shareUrl} onFocus={e => e.target.select()} />
                    <button type="button" className="admin-btn primary sm" onClick={copyShare}>複製</button>
                    <button type="button" className="admin-btn ghost sm" onClick={genShare}>重新產生</button>
                    <button type="button" className="admin-btn danger sm" onClick={revokeShare}>停用</button>
                  </div>
                  <small style={{ display: 'block', marginTop: 6, color: 'var(--ink-soft)', fontSize: 12 }}>
                    拿到這條連結的人不需登入就能看到此商品。重新產生或停用後，舊連結立即失效。
                  </small>
                </>
              ) : (
                <>
                  <button type="button" className="admin-btn primary sm" onClick={genShare}>產生分享連結</button>
                  <small style={{ display: 'block', marginTop: 6, color: 'var(--ink-soft)', fontSize: 12 }}>
                    產生一條免登入連結，可傳給非會員檢視此商品。
                  </small>
                </>
              )}
            </div>
          )}

          {/* 變體（種類 / 角色）編輯器 */}
          <div className="field">
            <label>種類 / 角色變體（選填，各自價格）</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {form.variants.map((v, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input style={{ flex: 2 }} type="text" placeholder="角色A / DARTSLIVE 3 版" value={v.name} onChange={e => setVariant(i, 'name', e.target.value)} />
                  <input style={{ flex: 1 }} type="number" min="0" step="1" placeholder="價格" value={v.price} onChange={e => setVariant(i, 'price', e.target.value)} />
                  <button type="button" className="admin-btn danger sm" onClick={() => removeVariant(i)}>移除</button>
                </div>
              ))}
              <button type="button" className="admin-btn ghost sm" style={{ alignSelf: 'flex-start' }} onClick={addVariant}>＋ 新增變體</button>
            </div>
          </div>

          <div className="field">
            <label>商品說明</label>
            <textarea placeholder="採「站內登記 → 我們確認後通知付款」…" value={form.description} onChange={e => set('description', e.target.value)} />
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label className={`admin-check${form.status === 'active' ? ' on' : ''}`} style={{ flex: '1 1 160px' }}>
              <input type="checkbox" checked={form.status === 'active'} onChange={e => set('status', e.target.checked ? 'active' : 'draft')} />
              上架販售
            </label>
            <label className={`admin-check${form.tagNew ? ' on' : ''}`} style={{ flex: '1 1 160px' }}>
              <input type="checkbox" checked={form.tagNew} onChange={e => set('tagNew', e.target.checked)} />
              標記 NEW
            </label>
            <label className={`admin-check${form.tagLimited ? ' on' : ''}`} style={{ flex: '1 1 160px' }}>
              <input type="checkbox" checked={form.tagLimited} onChange={e => set('tagLimited', e.target.checked)} />
              標記 限定
            </label>
          </div>
        </div>

          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>{'\u81ea\u52d5\u4e0b\u67b6\u6642\u9593\uff08\u53f0\u7063\u6642\u9593\uff09'}</label>
            <input type="datetime-local" value={form.unpublishAt}
              onChange={e => set('unpublishAt', e.target.value)} />
            <small>{'\u9078\u586b\uff1b\u7559\u7a7a\u5c31\u6703\u6301\u7e8c\u4e0a\u67b6\u3002\u6642\u9593\u5230\u5f8c\u6703\u81ea\u52d5\u8f49\u70ba\u8349\u7a3f\u3002'}</small>
          </div>
      </div>

      <div className="form-actions">
        <button type="button" className="admin-btn ghost" onClick={onCancel} disabled={saving}>取消</button>
        <button type="button" className="admin-btn primary" onClick={submit} disabled={saving}>{saving ? '儲存中…' : (editId ? '儲存變更' : '儲存並上架')}</button>
      </div>
    </div>
  );
}

export default AdminVirtualPage;
